import { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { captureError } from '@/utils/captureError';
import { doc, onSnapshot, setDoc, updateDoc, serverTimestamp, type Timestamp } from 'firebase/firestore';
import { db } from '@/firebase';
import { useAuth } from '@/context/AuthContext';
import { trackEvent } from '@/config/analytics';
import { isAccountDeletionInProgress } from '@/utils/accountDeletion';
import { stampMissingCurrency } from '@/services/currencyMigrationService';

// ── Types ──

export type BuiltInCurrency = 'AUD' | 'USD' | 'EUR' | 'GBP' | 'JPY';
export type Currency = BuiltInCurrency;
export type Tier = 'free' | 'premium';

/** Default foreign-to-home rates seeded per home currency.
 *  Convention: rates[FROM] = "1 FROM = X HOME" (foreign-to-home multiplier). */
export const DEFAULT_RATES: Record<BuiltInCurrency, Record<string, number>> = {
  AUD: { USD: 1.43, EUR: 1.65, GBP: 1.90, JPY: 0.009 },
  USD: { AUD: 0.70, EUR: 1.15, GBP: 1.33, JPY: 0.006 },
  EUR: { AUD: 0.61, USD: 0.87, GBP: 1.15, JPY: 0.005 },
  GBP: { AUD: 0.53, USD: 0.75, EUR: 0.87, JPY: 0.005 },
  JPY: { AUD: 111.11, USD: 158.89, EUR: 183.33, GBP: 211.11 },
};

interface UserProfile {
  currency: Currency;
  conversionRates: Record<string, number>;
  customCurrencies: string[];
  onboardingComplete: boolean;
  createdAt: Timestamp | null;
  tier: Tier;
  stripeCustomerId: string | null;
  subscriptionId: string | null;
  subscriptionStatus: string | null;
  upgradedAt: Timestamp | null;
  cancelAtPeriodEnd: boolean;
  cancelAt: string | null;
  currencyBackfillComplete: boolean;
  /** Comped account — permanent premium access, independent of any store. */
  lifetimeAccess: boolean;
  /** Which store the active (or most recent) subscription came from.
   * Server-written only, like the other subscription fields — see
   * PROTECTED_FIELDS in functions/src/stripe.ts and firestore.rules. Null
   * until any real purchase has ever happened. */
  subscriptionSource: 'stripe' | 'play' | null;
}

interface ProfileContextValue {
  profile: UserProfile;
  profileLoading: boolean;
  isPremium: boolean;
  hasSubscription: boolean;
  updateCurrency: (currency: Currency) => Promise<void>;
  updateConversionRates: (rates: Record<string, number>) => Promise<void>;
  updateCustomCurrencies: (currencies: string[]) => Promise<void>;
  markOnboardingComplete: () => Promise<void>;
}

const DEFAULT_PROFILE: UserProfile = {
  currency: 'AUD',
  conversionRates: DEFAULT_RATES['AUD'],
  customCurrencies: [],
  onboardingComplete: false,
  createdAt: null,
  tier: 'free',
  stripeCustomerId: null,
  subscriptionId: null,
  subscriptionStatus: null,
  upgradedAt: null,
  cancelAtPeriodEnd: false,
  cancelAt: null,
  currencyBackfillComplete: false,
  lifetimeAccess: false,
  subscriptionSource: null,
};

// ── Context ──

const ProfileContext = createContext<ProfileContextValue | null>(null);

export function useProfile(): ProfileContextValue {
  const ctx = useContext(ProfileContext);
  if (!ctx) throw new Error('useProfile must be used within ProfileProvider');
  return ctx;
}

/** Safe version that returns null outside ProfileProvider (for shared/public pages) */
export function useProfileSafe(): ProfileContextValue | null {
  return useContext(ProfileContext);
}

// ── Provider ──

export const ProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile>(DEFAULT_PROFILE);
  const [profileLoading, setProfileLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setProfile(DEFAULT_PROFILE);
      setProfileLoading(false);
      return;
    }

    const profileRef = doc(db, 'users', user.uid, 'profile', 'preferences');

    const unsubscribe = onSnapshot(profileRef, async (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        const currency = (data.currency ?? DEFAULT_PROFILE.currency) as Currency;
        setProfile({
          currency,
          conversionRates: data.conversionRates ?? DEFAULT_RATES[currency] ?? DEFAULT_RATES['AUD'],
          customCurrencies: data.customCurrencies ?? [],
          onboardingComplete: data.onboardingComplete ?? DEFAULT_PROFILE.onboardingComplete,
          createdAt: data.createdAt ?? null,
          tier: data.tier ?? DEFAULT_PROFILE.tier,
          stripeCustomerId: data.stripeCustomerId ?? null,
          subscriptionId: data.subscriptionId ?? null,
          subscriptionStatus: data.subscriptionStatus ?? null,
          upgradedAt: data.upgradedAt ?? null,
          cancelAtPeriodEnd: data.cancelAtPeriodEnd ?? false,
          cancelAt: data.cancelAt ?? null,
          currencyBackfillComplete: data.currencyBackfillComplete ?? false,
          lifetimeAccess: data.lifetimeAccess ?? false,
          subscriptionSource: data.subscriptionSource ?? null,
        });
      } else {
        // Account deletion removes the profile doc while this listener is
        // still mounted — re-creating it here would resurrect the doc and
        // fire a phantom sign_up. Skip bootstrap while a deletion runs.
        if (isAccountDeletionInProgress()) return;
        // New user — create default profile doc
        // Note: tier is omitted here (protected field in Firestore rules)
        // and defaults to 'free' on read via ?? fallback
        await setDoc(profileRef, {
          currency: DEFAULT_PROFILE.currency,
          conversionRates: DEFAULT_PROFILE.conversionRates,
          customCurrencies: DEFAULT_PROFILE.customCurrencies,
          onboardingComplete: DEFAULT_PROFILE.onboardingComplete,
          createdAt: serverTimestamp(),
        }, { merge: true });
        trackEvent('sign_up');
      }
      setProfileLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  const updateCurrency = useCallback(async (newCurrency: Currency) => {
    if (!user) return;
    const oldCurrency = profile.currency;

    // Stamp wines lacking priceCurrency with the OLD currency before switching
    if (oldCurrency !== newCurrency) {
      try {
        await stampMissingCurrency(user.uid, oldCurrency);
      } catch (e) {
        captureError(e, { context: 'profile' });
        // Non-blocking — proceed with currency change
      }
    }

    const profileRef = doc(db, 'users', user.uid, 'profile', 'preferences');
    // Seed default rates for the new home currency
    const newRates = DEFAULT_RATES[newCurrency] ?? {};
    await updateDoc(profileRef, { currency: newCurrency, conversionRates: newRates });
  }, [user, profile.currency]);

  const updateConversionRates = useCallback(async (rates: Record<string, number>) => {
    if (!user) return;
    const profileRef = doc(db, 'users', user.uid, 'profile', 'preferences');
    await updateDoc(profileRef, { conversionRates: rates });
  }, [user]);

  const updateCustomCurrencies = useCallback(async (currencies: string[]) => {
    if (!user) return;
    const profileRef = doc(db, 'users', user.uid, 'profile', 'preferences');
    await updateDoc(profileRef, { customCurrencies: currencies });
  }, [user]);

  const markOnboardingComplete = useCallback(async () => {
    if (!user) return;
    const profileRef = doc(db, 'users', user.uid, 'profile', 'preferences');
    await updateDoc(profileRef, { onboardingComplete: true });
  }, [user]);

  // ── One-time backfill: stamp wines missing priceCurrency with home currency ──
  // Skip if profile already has the flag set (avoids reading entire wines collection every session)
  const backfillRan = useRef(false);
  useEffect(() => {
    if (!user || profileLoading || backfillRan.current) return;
    if (profile.currencyBackfillComplete) return;
    backfillRan.current = true;
    (async () => {
      try {
        const stamped = await stampMissingCurrency(user.uid, profile.currency);
        // Mark backfill complete so future sessions skip this
        const profileRef = doc(db, 'users', user.uid, 'profile', 'preferences');
        await updateDoc(profileRef, { currencyBackfillComplete: true });
        if (stamped > 0) console.info(`Currency backfill: stamped ${stamped} wines`);
      } catch (e) {
        captureError(e, { context: 'profile' });
      }
    })();
  }, [user, profileLoading, profile.currency, profile.currencyBackfillComplete]);

  // Comped accounts stay premium regardless of tier/Stripe state
  const isPremium = profile.tier === 'premium' || profile.lifetimeAccess;
  const hasSubscription = !!profile.subscriptionId;

  const value = useMemo<ProfileContextValue>(() => ({
    profile,
    profileLoading,
    isPremium,
    hasSubscription,
    updateCurrency,
    updateConversionRates,
    updateCustomCurrencies,
    markOnboardingComplete,
  }), [profile, profileLoading, isPremium, hasSubscription, updateCurrency, updateConversionRates, updateCustomCurrencies, markOnboardingComplete]);

  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  );
};
